import { useEffect, useState } from 'react';
import '../../App.css';
import '../../components/css/CustomElements.css';
import useResponsive from '../../hooks/useResponsive';
import onboardingData from '../../data/mockOnboard.json';
import { useNavigate, useParams } from 'react-router-dom';
import JobCard from '../../components/JobCard';
import { useAudio } from '../../hooks/useAudio';
import { useCamera } from '../../hooks/useCamera';
import axios from 'axios';
import Spinner from '../../components/Spinner';
import { clearAllItems, getWithExpiry, setWithExpiry } from '../../utils/sessionStorage';
import { getApplicationByApplicantGuid, getApplicationByJobId, initializeAnswersForInterview } from '../../api/ApplicationService';
import { FormBuilderService } from '../../api/FormBuilderService';
import { MediaService } from '../../api/MediaService';
import ToastMessage, { ToastMessageProps } from '../../components/ToastMessage';
import { useInternetConnection } from '../../utils/connection';


const Onboarding = () => {
  const isMobile = useResponsive('md');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const navigate = useNavigate();

  const [application, setApplication] = useState(null);
  const [job, setJob] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [interviewId, setInterviewId] = useState(null);
  const [hasQuestionnaire, setHasQuestionnaire] = useState(true);
  const [contentType, setContentType] = useState(null);
  const [ttsLoading, setTtsLoading] = useState(true);
  const [processingButtons, setProcessingButtons] = useState<{[key: string]: boolean}>({
    audioTest: false,
    videoTest: false,
    startInterview: false,
  });
  const [showToast, setShowToast] = useState(false);
  const [toast, setToast] = useState<ToastMessageProps>({
    hasTitle: false,
    title: '',
    message: '',
    type: 'info',
    isMobile: isMobile,
    isOpen: false,
  });

  const isConnected = useInternetConnection();

  useEffect(() => {
    if (!isConnected) {
      const connectionMessage = `You are offline. Please reconnect to continue.(${Date.now()})`;
      setToast({
        hasTitle: true,
        title: 'Connection Lost',
        message: connectionMessage.replace(/\(\d+\)$/, ''),
        type: 'error',
        isMobile: isMobile,
        isOpen: true,
      });
      setShowToast(true);
    } else {
      setShowToast(false);
      setError(null);
    }
  }, [isConnected]);

  const { applicantGuid } = useParams();

  const removeCookie = (name: string) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  };

  // use camera and audio hooks
  const { 
    isCameraOn, 
    videoRef, 
    toggleCamera, 
    devices: videoDevices, 
    selectedDeviceId: selectedVideoDeviceId, 
    setSelectedDeviceId: setSelectedVideoDeviceId,
    startCamera,
    error: cameraError
  } = useCamera();


  // show toast when camera error occurs
  useEffect(() => {
    if (cameraError) {
      console.log("cameraError", cameraError);
      setToast({
        hasTitle: true,
        title: 'Camera Error',
        message: cameraError.replace(/ \(\d+\)$/, ''),
        type: 'error',
        isMobile: isMobile,
        isOpen: true,
      });
      setShowToast(true);
    }
  }, [cameraError]);
  
  const { 
    isAudioActive, 
    audioLevel, 
    toggleAudio, 
    audioDevices, 
    selectedAudioDeviceId, 
    setSelectedAudioDeviceId,
    startAudio,
    error: audioError
  } = useAudio();

  // show toast when audio error occurs
  useEffect(() => {
    if (audioError) {
      console.log(audioError);
      setShowToast(false); // Close any existing toast
      setTimeout(() => {
        setToast({
          hasTitle: true,
          title: 'Microphone Error',
          message: audioError.replace(/ \(\d+\)$/, ''),
          type: 'error',
          isMobile: isMobile,
          isOpen: true,
        });
        setShowToast(true);
      }, 10);
    }
  }, [audioError]);

  // Handle device change
  const handleCameraDeviceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newDeviceId = e.target.value;
    setSelectedVideoDeviceId(newDeviceId);
    console.log('newDeviceId', newDeviceId);

    // store to persist so that we can use later for interview
    setWithExpiry('selectedCameraDeviceId', newDeviceId, 24 * 60 * 60 * 1000); // 24 hours
    
    // If camera is already on, restart it with the new device
    if (isCameraOn) {
      toggleCamera(); // Stop current camera
      setTimeout(() => startCamera(newDeviceId), 100); // Start with new device
    }
  };

  const handleAudioDeviceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newDeviceId = e.target.value;
    setSelectedAudioDeviceId(newDeviceId);
    console.log('newDeviceId', newDeviceId);

    // store to persist so that we can use later for audio interview
    setWithExpiry('selectedAudioDeviceId', newDeviceId, 24 * 60 * 60 * 1000); // 24 hours
    
    // If audio is already active, restart it with the new device
    if (isAudioActive) {
      toggleAudio(); // Stop current audio
      setTimeout(() => startAudio(newDeviceId), 100); // Start with new device
    }
  };

  // general error handling
  useEffect(() => {
    if (error) {
      console.log("error", error);
      setToast({
        hasTitle: true,
        title: 'Error',
        message: error.replace(/ \(\d+\)$/, ''),
        type: 'error',
        isMobile: isMobile,
        isOpen: true,
      });
      setShowToast(true);
    }
  }, [error]);

  useEffect(() => {
    const fetchApplicationData = async () => {
      if (!applicantGuid) return;

      setIsLoading(true);
      setError(null);

      try {
        // for demo we null the jtToken
        removeCookie('jtToken'); // we remove this later

        // Fetch application data
        const applicationResponse = await getApplicationByApplicantGuid(applicantGuid || '');
        if (applicationResponse && applicationResponse.length > 0) {
          // Access the first application in the array
          const applicationData = applicationResponse[0];
          if(applicationData.status === "Completed"){
            console.warn("Interview already completed");
            navigate('/interview/already-completed');
            return;
          }
          clearAllItems();
          
          setWithExpiry('applicantGuid', applicationData.applicationGuid, 1000 * 60 * 60 * 24);
          initializeAnswersForInterview(applicationData.applicationGuid);
          setWithExpiry('jobId', applicationData.jobId, 1000 * 60 * 60 * 24);
          setWithExpiry('interviewId', applicationData._id, 1000 * 60 * 60 * 24);

          // If job ID exists, fetch job data
          if (applicationData.jobId) {
            const jobResponse = await getApplicationByJobId(applicationData.jobId);
            setWithExpiry('companyId', jobResponse.companyId.toString(), 1000 * 60 * 60 * 24);
            setWithExpiry('job', jobResponse, 1000 * 60 * 60 * 24);
            setJob(jobResponse);

            //check if it has a questionnaire
            const questionnaireResponse = await FormBuilderService.checkJobQuestionnaire(applicationData.jobId);
            if(questionnaireResponse.hasQuestionnaire === true && questionnaireResponse.data.active === true) {
              const questionnaires = questionnaireResponse.data.questions;
              const questionTexts = questionnaires.map((question: any) => question.questionText);
              const ttsAudio = await MediaService.generateTTSAudio(questionTexts, applicationData.applicationGuid);
              setTtsLoading(false);
              // console.log("done with tts", ttsAudio);
              setWithExpiry('ttsAudio', ttsAudio.data.audioFilePaths, 1000 * 60 * 60 * 24);
              setWithExpiry('questionnaires', questionnaires, 1000 * 60 * 60 * 24);
              setContentType(questionnaireResponse.data.contentType.toString().toLowerCase());
              const interviewStatus = {
                status: "Incomplete"
              };
              axios.patch(`/api/v1/interview/${applicantGuid}`, interviewStatus);
            }
            else{
              setHasQuestionnaire(false);
            }
          }

        } else {
          console.error('No application found for this applicant');
          setError(`No application found for this applicant. (${Date.now()})`);
          // Handle the case where no application was found
        }

        setApplication(applicationResponse);

      } catch (error: any) {
        setError(`There was an error fetching the application data. Please try again later. (${Date.now()})`);
        console.error('Error fetching data:', error);
        // navigate('/404');
      } finally {
        setIsLoading(false);
      }
    };

    fetchApplicationData();
  }, [applicantGuid, navigate]);

  useEffect(() => {
    const initializeCamera = async () => {
      try {
        console.log('Onboarding: Initial camera setup');
        await toggleCamera();
        console.log('Onboarding: Camera initialized on mount');
      } catch (error) {
        console.error('Onboarding: Failed to initialize camera:', error);
      }
    }
    initializeCamera();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleButtonClick = async (buttonId: string, action: () => Promise<void>) => {
    const startTime = new Date();
    try {
      if (showToast) {
        setShowToast(false);
      }
      console.log(buttonId, ' Starting button action', new Date().toISOString());
      setProcessingButtons(prev => ({ ...prev, [buttonId]: true }));
      console.log(buttonId, ' Button action started', new Date().toISOString());
      await action();
      console.log(buttonId, ' Button action completed', new Date().toISOString());
    } finally {
      console.log(buttonId, ' Button action finally', new Date().toISOString());
      console.log("Time taken:", new Date().getTime() - startTime.getTime(), "ms");
      setProcessingButtons(prev => ({ ...prev, [buttonId]: false }));
    }
  };

  const handleStartInterview = async () => {
    await new Promise(resolve => setTimeout(resolve, 250));
    if(!termsAccepted || ttsLoading || !isConnected){
      if(!isConnected){
        const connectionMessage = `You are offline. Please reconnect to continue.(${Date.now()})`;
        setToast({
          hasTitle: true,
          title: 'Connection Lost',
          message: connectionMessage.replace(/\(\d+\)$/, ''),
          type: 'error',
          isMobile: isMobile,
          isOpen: true,
        });
        setShowToast(true);
      }
      return;
    }

    if(contentType === 'audio'){
      navigate('/interview/questions/audio');
    }else{
      navigate('/interview/questions');
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        padding: isMobile ? '1rem' : '2rem',
        paddingTop: '0',
      }}
    >
      {showToast && (
        <ToastMessage {...toast}
          isMobile={isMobile}
          onClose={() => setShowToast(false)}
        />
      )}
      <div
        style={{
          width: isMobile ? '100%' : '70%',
          padding: '0rem',
          borderRadius: '8px',
          marginTop: '0',
        }}
      >
        {isLoading ? (
          <Spinner />
        ) : job ? (
          <JobCard job={job} isMobile={isMobile} />
        ) : (
          <div>No job information available</div>
        )}

        {!hasQuestionnaire && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            marginTop: '2.5rem',
          }}>
            <h3>This job does not have a questionnaire.</h3>
            <p>Please contact the recruiter for more information.</p>
          </div>
        )}

        {hasQuestionnaire && (<>
        {/* Interview Instructions and Camera Section */}
        <div
          style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: '2rem',
            marginTop: '1.5rem',
          }}
        >
          {/* Left Section - Instructions */}
          <div
            style={{
              flex: '1',
              textAlign: 'left',
            }}
          >
            <h2
              style={{
                fontSize: '1.3rem',
                fontWeight: '600',
                color: '#333',
                marginTop: '0',
                marginBottom: '1rem',
              }}
            >
              {onboardingData.interview.heading}
            </h2>

            {onboardingData.interview.intro.map((paragraph, index) => (
              <p
                key={index}
                style={{
                  color: '#555',
                  fontSize: '0.95rem',
                  marginBottom: '0.75rem',
                }}
              >
                {paragraph}
              </p>
            ))}

            <h3
              style={{
                fontSize: '1.1rem',
                fontWeight: '600',
                marginBottom: '0.75rem',
              }}
            >
              {onboardingData.interview.tipsHeading}
            </h3>

            <ul
              style={{
                listStyleType: 'disc',
                paddingLeft: '1.5rem',
                color: '#555',
                fontSize: '0.95rem',
              }}
            >
              {onboardingData.interview.tips.map((tip, index) => (
                <li key={index} style={{ marginBottom: '0.5rem' }}>
                  {tip}
                </li>
              ))}
            </ul>
            <p>
            {onboardingData.interview.subHeading}
              
            </p>
          </div>

          {/* Right Section - Camera Preview */}
          <div
            style={{
              flex: '1',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                width: '100%',
                aspectRatio: '4/3',
                backgroundColor: '#f5f5f5',
                border: '1px solid #e0e4e8',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {/* Always render the video element but control visibility */}
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: isCameraOn ? 'block' : 'none',
                }}
              />

              {/* Show placeholder only when camera is off */}
              {!isCameraOn && (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    backgroundImage: `url(${onboardingData.assets.cameraPlaceholder})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#999',
                  }}
                ></div>
              )}
            </div>

            {/* Audio level indicator */}
            {isAudioActive && (
              <div
                style={{
                  width: '100%',
                  height: '8px',
                  backgroundColor: '#e0e4e8',
                  marginTop: '0.5rem',
                  borderRadius: '4px',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${audioLevel}%`,
                    height: '100%',
                    backgroundColor: '#2596be',
                    transition: 'width 0.01s',
                  }}
                />
              </div>
            )}

            {/* Test Buttons */}
            <div
              style={{
                display: 'flex',
                gap: '1rem',
                marginBottom: '0.6rem',
                width: '100%',
                justifyContent: 'center',
                marginTop: '1rem',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', width: isMobile ? '100%' : '50%' }}>
                <button
                  className={`test-button ${isAudioActive ? 'active' : ''}`}
                  onClick={() => handleButtonClick('audioTest', async () => {
                    await toggleAudio(selectedAudioDeviceId);
                  })}
                  id="test-audio"
                  disabled={processingButtons.audioTest}
                  style={{ 
                    fontSize: isMobile ? '0.7rem' : '0.8rem',
                    width: '100%',
                    minHeight: '40px',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {processingButtons.audioTest ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <img src="/svgs/micactive.svg" alt="Microphone" />
                      {isAudioActive ? 'Stop Audio Test' : onboardingData.buttons.testAudio}
                    </>
                  )}
                </button>

                {/* Audio Device Selector Mobile*/}
                {audioDevices.length >= 1 && isMobile && (
                  <div style={{
                    marginTop: '8px',
                    width: '100%',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    <select
                      value={selectedAudioDeviceId}
                      onChange={handleAudioDeviceChange}
                      id="select-audioDevice"
                      style={{
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        fontSize: '0.6rem',
                        width: '100%',
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        appearance: 'menulist',
                        WebkitAppearance: 'menulist',
                      }}
                    >
                      {audioDevices.map((device) => {
                        let displayName = device.label || `Microphone ${audioDevices.indexOf(device) + 1}`;
                        
                        // Truncate long device names
                        if (displayName.length > 30) {
                          displayName = displayName.substring(0, 27) + '...';
                        }
                        
                        return (
                          <option key={device.deviceId} value={device.deviceId}>
                            {displayName}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                )}

                {/* Audio Device Selector Desktop*/}
                {audioDevices.length >= 1 && !isMobile && (
                  <select
                    value={selectedAudioDeviceId}
                    onChange={handleAudioDeviceChange}
                    id="select-audioDevice"
                    style={{
                      marginTop: '8px',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      fontSize: '0.6rem',
                      width: '100%'
                    }}
                  >
                    {audioDevices.map((device) => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label || `Microphone ${audioDevices.indexOf(device) + 1}`}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Camera Device Selector */}
              <div style={{ display: 'flex', flexDirection: 'column', width: isMobile ? '100%' : '50%' }}>
              <button
                className={`test-button ${isCameraOn ? 'active' : ''}`}
                onClick={() => handleButtonClick('videoTest', async () => {
                  await toggleCamera(selectedVideoDeviceId);
                })}
                id="test-video"
                disabled={processingButtons.videoTest}
                style={{ 
                  fontSize: isMobile ? '0.7rem' : '0.8rem',
                  width: '100%',
                  minHeight: '40px',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis'
                }}              >
                
                {processingButtons.videoTest ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <img src={onboardingData.assets.cameraIcon} alt="Camera" />
                      {isCameraOn ? 'Stop Camera' : onboardingData.buttons.testCamera}
                    </>
                  )}
              </button>

                {/* Camera Device Selector Mobile*/}
                {videoDevices.length >= 1 && isMobile && (
                  <div style={{
                    marginTop: '8px',
                    width: '100%',
                    maxWidth: '100vw',
                    overflow: 'hidden',
                    position: 'relative',
                    boxSizing: 'border-box',
                  }}>
                    <select
                      value={selectedVideoDeviceId}
                      onChange={handleCameraDeviceChange}
                      id="select-videoDevice"
                      style={{
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        fontSize: '0.6rem',
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: 0,
                        boxSizing: 'border-box',
                        appearance: 'menulist',
                        WebkitAppearance: 'menulist',
                      }}
                    >
                      {videoDevices.map((device) => {
                        // Clean up device label to make it user-friendly
                        let displayName = device.label  || `Camera ${videoDevices.indexOf(device) + 1}`;
                        
                        // Remove device IDs and technical details (anything in parentheses with hex codes)
                        displayName = displayName
                          .replace(/\([0-9a-f]+:[0-9a-f]+\)/i, '')  // Remove hex IDs like (04d:0825)
                          .replace(/\(id=.*?\)/i, '')              // Remove ID references
                          .replace(/\(\w+\s+\w+\)/i, '')           // Other technical parenthetical references
                          .replace(/\s{2,}/g, ' ')                 // Replace multiple spaces with single space
                          .trim();
                        
                        // If the name became too short after cleaning, use a generic name
                        if (displayName.length < 3) {
                          displayName = `Camera ${videoDevices.indexOf(device) + 1}`;
                        }
                        
                        // Truncate long device names for mobile - be more aggressive
                        if (displayName.length > 20) {
                          displayName = displayName.substring(0, 17) + '...';
                        }
                        
                        return (
                          <option key={device.deviceId} value={device.deviceId}>
                            {displayName}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                )}

                {/* Camera Device Selector Desktop*/}
                {videoDevices.length >= 1 && !isMobile && (
                  <select
                    value={selectedVideoDeviceId}
                    onChange={handleCameraDeviceChange}
                    id="select-videoDevice"
                    style={{
                      marginTop: '8px',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      fontSize: '0.6rem',
                      width: '100%'
                    }}
                  >
                    {videoDevices.map((device) => {
                      // Clean up device label to make it user-friendly
                      let displayName = device.label || `Camera ${videoDevices.indexOf(device) + 1}`;
                      
                      // Remove device IDs and technical details (anything in parentheses with hex codes)
                      displayName = displayName
                        .replace(/\([0-9a-f]+:[0-9a-f]+\)/i, '')  // Remove hex IDs like (04d:0825)
                        .replace(/\(id=.*?\)/i, '')              // Remove ID references
                        .replace(/\(\w+\s+\w+\)/i, '')           // Other technical parenthetical references
                        .replace(/\s{2,}/g, ' ')                 // Replace multiple spaces with single space
                        .trim();
                      
                      // If the name became too short after cleaning, use a generic name
                      if (displayName.length < 3) {
                        displayName = `Camera ${videoDevices.indexOf(device) + 1}`;
                      }
                      
                      return (
                        <option key={device.deviceId} value={device.deviceId}>
                          {displayName}
                        </option>
                      );
                    })}
                  </select>
                )}
              </div>
            </div>

            <span style={{ fontSize: '0.8rem', color: '#6c757d', marginBottom: '1rem', textAlign: 'left' }}>Please choose your recording devices to start the interview.</span>

            {/* Terms and Start Interview */}
            <div style={{ width: '100%', position: 'relative' }}>
              <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5rem',
                fontSize: '0.7rem',
                color: '#6c757d',
                marginBottom: '1rem',
                textAlign: 'left',
              }}
              >
              <input
                type="checkbox"
                id="termsAgreement"
                checked={termsAccepted}
                onChange={() => setTermsAccepted(!termsAccepted)}
                disabled={isLoading}
                style={{
                width: '1.2rem',
                height: '1.2rem',
                backgroundColor: '#0d6efd',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                }}
              />
              <label htmlFor="termsAgreement" style={{ flex: 1 }}>
                {onboardingData.legal.text}{' '}
                <a
                href={onboardingData.legal.terms.url}
                style={{ color: '#0d6efd', textDecoration: 'underline' }}
                target="_blank"
                rel="noopener noreferrer"
                >
                {onboardingData.legal.terms.text}
                </a>
                {' '}and{' '}
                <a
                href={onboardingData.legal.privacy.url}
                style={{ color: '#0d6efd', textDecoration: 'underline' }}
                target="_blank"
                rel="noopener noreferrer"
                >
                {' '}
                {onboardingData.legal.privacy.text}
                </a>
                {' '}{onboardingData.legal.text2}{' '}
              </label>
              </div>
              <button
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: '#0d6efd',
                border: 'none',
                borderRadius: '4px',
                color: 'white',
                fontSize: '1rem',
                fontWeight: '500',
                cursor: termsAccepted && videoDevices.length > 0 && audioDevices.length > 0 ? 'pointer' : 'not-allowed',
                opacity: termsAccepted && videoDevices.length > 0 && audioDevices.length > 0 ? '1' : '0.65',
              }}
              disabled={!(termsAccepted && videoDevices.length > 0 && audioDevices.length > 0)}
              onClick={() => handleButtonClick('startInterview', handleStartInterview)}
              id={`start-interview-${applicantGuid}`}
              >
              {isLoading || processingButtons.startInterview ? <Spinner size="sm" color="light" /> : onboardingData.buttons.startInterview}
              </button>
            </div>
          </div>
        </div> 
        </>)}
        
      </div>
    </div>
  );
};

export default Onboarding;
